var VehicleFitmentSearch = class extends HTMLElement {
  constructor() {
    super();
    this._onSelectChanged = this._onSelectChanged.bind(this);
    this._onResetClicked = this._onResetClicked.bind(this);
    this._onVariantPickerReady = this._onVariantPickerReady.bind(this);
    this._onProductVariantChanged = this._onProductVariantChanged.bind(this);
    this._onPageShow = this._onPageShow.bind(this);
  }
  connectedCallback() {
    if (this._initialized) {
      return;
    }
    this._initialized = true;
    this.selects = {
      make: this.querySelector('[data-fitment-select="make"]'),
      model: this.querySelector('[data-fitment-select="model"]'),
      year: this.querySelector('[data-fitment-select="year"]')
    };
    this.resultElement = this.querySelector("[data-fitment-result]");
    this.resetButton = this.querySelector("[data-fitment-reset]");
    this.cartPropertyInput = this.querySelector("[data-fitment-cart-property]");
    this.sizePropertyInput = this.querySelector("[data-fitment-size-property]");
    this.formId = this.getAttribute("form");
    this.storageKey = this._getStorageKey();
    this.config = this._readConfig();
    this.entries = [];
    this._entriesLoaded = false;
    this._entriesLoadFailed = false;
    Object.values(this.selects).forEach((select) => {
      if (select) {
        select.addEventListener("change", this._onSelectChanged);
      }
    });
    if (this.resetButton) {
      this.resetButton.addEventListener("click", this._onResetClicked);
    }
    document.addEventListener("variant-picker:ready", this._onVariantPickerReady);
    document.addEventListener("variant:change", this._onProductVariantChanged);
    window.addEventListener("pageshow", this._onPageShow);
    this._captureVariantWizardBaselineState();
    window.requestAnimationFrame(() => this._captureVariantWizardBaselineState());
    this._loadEntries().then((entries) => {
      this.entries = entries;
      this._entriesLoaded = true;
      this._initializeState();
    });
  }
  disconnectedCallback() {
    Object.values(this.selects || {}).forEach((select) => {
      if (select) {
        select.removeEventListener("change", this._onSelectChanged);
      }
    });
    if (this.resetButton) {
      this.resetButton.removeEventListener("click", this._onResetClicked);
    }
    document.removeEventListener("variant-picker:ready", this._onVariantPickerReady);
    document.removeEventListener("variant:change", this._onProductVariantChanged);
    window.removeEventListener("pageshow", this._onPageShow);
    this._clearVariantWizardFitment();
    this._stopVariantWizardResetRestore();
  }
  _readConfig() {
    const configNode = this.querySelector("[data-fitment-config]");
    if (!configNode) {
      return {
        rawData: "",
        placeholders: {},
        messages: {}
      };
    }
    try {
      return JSON.parse(configNode.textContent);
    } catch {
      return {
        rawData: "",
        placeholders: {},
        messages: {}
      };
    }
  }
  _getStorageKey() {
    const productKey = this.dataset.productId || this.formId || window.location.pathname;
    return `vehicle-fitment:${productKey}`;
  }
  _readStoredSelection() {
    if (!this.storageKey) {
      return {};
    }
    try {
      const storedSelection = window.sessionStorage.getItem(this.storageKey);
      return storedSelection ? JSON.parse(storedSelection) : {};
    } catch {
      return {};
    }
  }
  _persistSelection() {
    if (!this.storageKey) {
      return;
    }
    const selection = this._getSelection();
    try {
      if (selection.make || selection.model || selection.year) {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify({
          make: selection.make,
          model: selection.model,
          year: selection.year
        }));
      } else {
        window.sessionStorage.removeItem(this.storageKey);
      }
    } catch {
    }
  }
  async _loadEntries() {
    this._entriesLoadFailed = false;
    if (this.config.assetUrl) {
      try {
        const payload = await this._loadAssetPayload(this.config.assetUrl);
        if (payload) {
          return this._parsePayload(payload);
        }
        this._entriesLoadFailed = true;
      } catch {
        this._entriesLoadFailed = true;
      }
      return [];
    }
    return this._parseEntries(this.config.rawData || "");
  }
  async _loadAssetPayload(assetUrl) {
    window.__vehicleFitmentJsonPromises = window.__vehicleFitmentJsonPromises || {};
    const cachedPayload = await this._resolveAssetPayloadPromise(window.__vehicleFitmentJsonPromises[assetUrl]);
    if (cachedPayload) {
      return cachedPayload;
    }
    delete window.__vehicleFitmentJsonPromises[assetUrl];
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const requestUrl = attempt === 0 ? assetUrl : this._withCacheBust(assetUrl, attempt);
      const payload = await this._fetchAssetJson(requestUrl, attempt > 0);
      if (payload) {
        window.__vehicleFitmentJsonPromises[assetUrl] = Promise.resolve(payload);
        return payload;
      }
      if (attempt < 2) {
        await this._waitForAsset(100 * (attempt + 1));
      }
    }
    return null;
  }
  async _resolveAssetPayloadPromise(payloadPromise) {
    if (!payloadPromise) {
      return null;
    }
    try {
      return await Promise.race([payloadPromise, this._waitForAsset(2500)]);
    } catch {
      return null;
    }
  }
  _fetchAssetJson(assetUrl, noStore = false) {
    return fetch(assetUrl, { credentials: "same-origin", cache: noStore ? "no-store" : "default" }).then((response) => {
      return response.ok ? response.json() : null;
    }).catch(() => null);
  }
  _withCacheBust(assetUrl, attempt) {
    try {
      const url = new URL(assetUrl, window.location.href);
      url.searchParams.set("_fitment_retry", `${Date.now()}-${attempt}`);
      return url.toString();
    } catch {
      return assetUrl;
    }
  }
  _waitForAsset(delay) {
    return new Promise((resolve) => window.setTimeout(() => resolve(null), delay));
  }
  _parsePayload(payload) {
    if (Array.isArray(payload)) {
      return this._parseJsonEntries(payload);
    }
    if (payload?.v === 2 && payload.m) {
      return this._parseCompactEntries(payload.m);
    }
    return this._parseJsonEntries(payload?.entries || []);
  }
  _parseCompactEntries(makeMap) {
    const parsed = [];
    Object.entries(makeMap || {}).forEach(([makeKey, models]) => {
      const make = `${makeKey || ""}`.trim();
      Object.entries(models || {}).forEach(([modelKey, rows]) => {
        const model = `${modelKey || ""}`.trim();
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          const yearsPart = `${Array.isArray(row) ? row[0] : row?.years || row?.year || ""}`.trim();
          if (!make || !model || !yearsPart) {
            return;
          }
          parsed.push({
            make,
            model,
            years: yearsPart,
            note: `${Array.isArray(row) ? row[3] || "" : row?.note || ""}`.trim(),
            size: `${Array.isArray(row) ? row[1] || "" : row?.newSize || row?.size || ""}`.trim(),
            legacySize: `${Array.isArray(row) ? row[2] || "" : row?.legacySize || row?.oldSize || ""}`.trim()
          });
        });
      });
    });
    return parsed;
  }
  _parseJsonEntries(entries) {
    const parsed = [];
    entries.forEach((entry) => {
      const make = `${entry.make || ""}`.trim();
      const model = `${entry.model || ""}`.trim();
      const yearsPart = `${entry.years || entry.year || ""}`.trim();
      if (!make || !model || !yearsPart) {
        return;
      }
      parsed.push({
        make,
        model,
        years: yearsPart,
        note: `${entry.note || ""}`.trim(),
        size: `${entry.newSize || entry.size || ""}`.trim(),
        legacySize: `${entry.legacySize || entry.oldSize || ""}`.trim()
      });
    });
    return parsed;
  }
  _parseEntries(rawData) {
    const lines = rawData.split(/\r?\n/).map((line) => line.trim()).filter((line) => line !== "" && !line.startsWith("#"));
    const entries = [];
    lines.forEach((line) => {
      const parts = line.split("|").map((part) => part.trim());
      if (parts.length < 3) {
        return;
      }
      const make = parts[0];
      const model = parts[1];
      const yearsPart = parts[2];
      const note = parts[3] || "";
      const size = parts[4] || "";
      const legacySize = parts[5] || "";
      if (!make || !model || !yearsPart) {
        return;
      }
      entries.push({
        make,
        model,
        years: yearsPart,
        note,
        size,
        legacySize
      });
    });
    return entries;
  }
  _getEntryYears(entry) {
    return this._expandYears(entry?.years || entry?.year || "");
  }
  _entryMatchesYear(entry, year) {
    return this._getEntryYears(entry).includes(year);
  }
  _expandYears(yearsPart) {
    const years = [];
    yearsPart.split(/[，,\/、;；&]/).map((token) => token.trim()).filter(Boolean).forEach((token) => {
      const rangeMatch = token.match(/^(\d{4})\s*[-–—~]\s*(\d{4})$/);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1], 10);
        const end = parseInt(rangeMatch[2], 10);
        const lower = Math.min(start, end);
        const upper = Math.max(start, end);
        for (let year = lower; year <= upper; year += 1) {
          years.push(String(year));
        }
        return;
      }
      years.push(token);
    });
    return this._uniqueValues(years);
  }
  _uniqueValues(values) {
    const seen = new Set();
    return values.filter((value) => {
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  }
  _initializeState() {
    if (!this.entries.length) {
      const storedSelection = this._readStoredSelection();
      const existingMakes = this._getSelectValues(this.selects.make);
      this._populateSelect(this.selects.make, existingMakes, this.config.placeholders.make, existingMakes.length === 0, this.selects.make?.value || storedSelection.make || "");
      this._populateSelect(this.selects.model, [], this.config.placeholders.model);
      this._populateSelect(this.selects.year, [], this.config.placeholders.year);
      this._setResult("empty", this.config.messages.empty || "");
      this._syncCartProperty(null);
      this._syncVariantPickerFilter(null);
      this._toggleResetButton();
      return;
    }
    const makes = this._uniqueValues(this.entries.map((entry) => entry.make));
    const storedSelection = this._readStoredSelection();
    const selectedMake = this.selects.make?.value || storedSelection.make || "";
    const selectedModel = this.selects.model?.value || storedSelection.model || "";
    const selectedYear = this.selects.year?.value || storedSelection.year || "";
    this._populateSelect(this.selects.make, makes, this.config.placeholders.make, false, selectedMake);
    if (this.selects.make?.value) {
      this._handleMakeChanged(selectedModel, selectedYear);
      return;
    }
    this._populateSelect(this.selects.model, [], this.config.placeholders.model);
    this._populateSelect(this.selects.year, [], this.config.placeholders.year);
    this._setResult("idle", this.config.messages.pending || "");
    this._syncCartProperty(null);
    this._syncVariantPickerFilter(null);
    this._toggleResetButton();
  }
  _populateSelect(select, values, placeholder, disabled = values.length === 0, selectedValue = select?.value || "") {
    if (!select) {
      return;
    }
    const previousValue = selectedValue || select.value;
    select.innerHTML = "";
    select.options.add(new Option(placeholder || "", ""));
    values.forEach((value) => {
      select.options.add(new Option(value, value, false, value === previousValue));
    });
    if (!values.includes(previousValue)) {
      select.value = "";
    }
    select.disabled = disabled;
  }
  _getSelectValues(select) {
    if (!select) {
      return [];
    }
    return Array.from(select.options).map((option) => option.value).filter(Boolean);
  }
  _onSelectChanged(event) {
    const target = event.target;
    if (target === this.selects.make) {
      this._handleMakeChanged();
      return;
    }
    if (target === this.selects.model) {
      this._handleModelChanged();
      return;
    }
    this._updateSelectionResult();
  }
  _handleMakeChanged(selectedModel = "", selectedYear = "") {
    if (!this._entriesLoaded) {
      this._setResult("idle", this.config.messages.pending || "");
      this._persistSelection();
      this._toggleResetButton();
      return;
    }
    const make = this.selects.make ? this.selects.make.value : "";
    const modelEntries = this.entries.filter((entry) => entry.make === make);
    const models = this._uniqueValues(modelEntries.map((entry) => entry.model));
    this._populateSelect(this.selects.model, models, this.config.placeholders.model, !make || models.length === 0, selectedModel);
    this._populateSelect(this.selects.year, [], this.config.placeholders.year);
    if (this.selects.model?.value) {
      this._handleModelChanged(selectedYear);
      return;
    }
    this._setResult("idle", this.config.messages.pending || "");
    this._syncCartProperty(null);
    this._resetFitmentSizeSelection();
    this._persistSelection();
    this._toggleResetButton();
  }
  _handleModelChanged(selectedYear = "") {
    if (!this._entriesLoaded) {
      this._setResult("idle", this.config.messages.pending || "");
      this._persistSelection();
      this._toggleResetButton();
      return;
    }
    const make = this.selects.make ? this.selects.make.value : "";
    const model = this.selects.model ? this.selects.model.value : "";
    const yearEntries = this.entries.filter((entry) => entry.make === make && entry.model === model);
    const years = this._uniqueValues(yearEntries.flatMap((entry) => this._getEntryYears(entry)));
    this._populateSelect(this.selects.year, years, this.config.placeholders.year, !make || !model || years.length === 0, selectedYear);
    if (this.selects.year?.value) {
      this._updateSelectionResult();
      return;
    }
    this._setResult("idle", this.config.messages.pending || "");
    this._syncCartProperty(null);
    this._resetFitmentSizeSelection();
    this._persistSelection();
    this._toggleResetButton();
  }
  _updateSelectionResult() {
    if (!this._entriesLoaded) {
      this._setResult("idle", this.config.messages.pending || "");
      this._persistSelection();
      this._toggleResetButton();
      return;
    }
    const baseSelection = this._getSelection();
    const selection = { ...baseSelection };
    if (!selection.make || !selection.model || !selection.year) {
      this._setResult("idle", this.config.messages.pending || "");
      this._syncCartProperty(null);
      this._resetFitmentSizeSelection();
      this._persistSelection();
      this._toggleResetButton();
      return;
    }
    const match = this.entries.find((entry) => entry.make === selection.make && entry.model === selection.model && this._entryMatchesYear(entry, selection.year));
    if (!match) {
      this._setResult("error", this._interpolate(this.config.messages.noMatch || "", selection));
      this._syncCartProperty(null);
      this._resetFitmentSizeSelection();
      this._persistSelection();
      this._toggleResetButton();
      return;
    }
    selection.size = match.size || "";
    selection.legacySize = match.legacySize || "";
    selection.note = match.note || "";
    this._setResult("success", this._interpolate(selection.note || this.config.messages.success || "", selection));
    this._syncCartProperty(selection);
    this._persistSelection();
    this._applyFitmentSelectionToVariantPicker(selection);
    this._toggleResetButton();
  }
  _getSelection() {
    return {
      make: this.selects.make ? this.selects.make.value : "",
      model: this.selects.model ? this.selects.model.value : "",
      year: this.selects.year ? this.selects.year.value : "",
      size: "",
      legacySize: "",
      note: "",
      vehicle: [this.selects.make ? this.selects.make.value : "", this.selects.model ? this.selects.model.value : "", this.selects.year ? this.selects.year.value : ""].filter(Boolean).join(" / ")
    };
  }
  _interpolate(template, selection) {
    return template.replace(/\{(vehicle|make|model|year|note|size|legacySize)\}/g, (match, token) => {
      return selection[token] || "";
    }).trim();
  }
  _setResult(state, message) {
    if (!this.resultElement) {
      return;
    }
    this.resultElement.dataset.state = state;
    this.resultElement.textContent = message;
    this.resultElement.hidden = this.config.showResultMessage === false || !message || state === "idle";
  }
  _normalizeValue(value) {
    return `${value || ""}`.trim().toUpperCase();
  }
  _getFitmentSizeRank(value) {
    return this._getDefaultFitmentSizeValues().indexOf(this._normalizeValue(value));
  }
  _isSizeOptionName(value) {
    const optionName = this._normalizeValue(value);
    return optionName === "SIZE" || optionName.includes("SIZE");
  }
  _getVariantPicker() {
    if (!this.formId) {
      return null;
    }
    const selector = `variant-picker[form="${CSS.escape(this.formId)}"]`;
    return this.closest(".product-info")?.querySelector(selector) || document.querySelector(selector);
  }
  _getProductContainer() {
    return this.closest(".product-info") || document.querySelector(".shopify-section--main-product .product-info") || document;
  }
  _getVariantOptionValues(variantPicker, position) {
    if (!position || !Array.isArray(variantPicker?.product?.variants)) {
      return [];
    }
    return this._uniqueValues(variantPicker.product.variants.map((variant) => variant.options?.[position - 1]).filter(Boolean));
  }
  _syncVariantPickerFilter(filter, displayValue = null) {
    this._beginFitmentSync();
    const variantPicker = this._getVariantPicker();
    const candidateValues = this._getFitmentFilterCandidates(filter, displayValue);
    const filterValue = filter?.value || candidateValues[0] || null;
    if (variantPicker) {
      if (filter && filter.position && filter.value) {
        variantPicker.setFitmentFilter?.(filter.position, filter.value);
      } else {
        variantPicker.clearFitmentFilter?.();
      }
    }
    if (!filterValue) {
      this._clearVariantWizardFitment();
    }
    this._syncProductVariationFilter(filterValue);
  }
  _beginFitmentSync() {
    this._fitmentSyncToken = (this._fitmentSyncToken || 0) + 1;
    return this._fitmentSyncToken;
  }
  _isSelectionCurrent(selection) {
    const currentSelection = this._getSelection();
    return currentSelection.make === selection.make && currentSelection.model === selection.model && currentSelection.year === selection.year;
  }
  async _waitForVariantPickerProduct(variantPicker) {
    if (variantPicker.product && Array.isArray(variantPicker.product.variants)) {
      return true;
    }
    if (variantPicker.productPromise) {
      try {
        variantPicker.product = await variantPicker.productPromise;
      } catch {
      }
    }
    return Boolean(variantPicker.product && Array.isArray(variantPicker.product.variants));
  }
  async _applyFitmentSelectionToVariantPicker(selection) {
    const candidateValues = this._getFitmentSizeCandidates(selection);
    const filterValue = candidateValues[0] || null;
    const syncToken = this._beginFitmentSync();
    const shouldSelectVariant = true;
    const variantPicker = this._getVariantPicker();
    if (!candidateValues.length) {
      this._clearVariantWizardFitment();
      this._syncProductVariationFilter(null);
      return null;
    }
    if (!variantPicker) {
      this._syncVariantWizardFitment({ optionValue: filterValue }, candidateValues, shouldSelectVariant);
      this._syncProductVariationFilter(filterValue);
      return {
        position: null,
        value: filterValue
      };
    }
    const hasProductData = await this._waitForVariantPickerProduct(variantPicker);
    if (syncToken !== this._fitmentSyncToken || !this._isSelectionCurrent(selection)) {
      return null;
    }
    if (!hasProductData) {
      this._syncVariantWizardFitment({ optionValue: filterValue }, candidateValues, shouldSelectVariant);
      this._syncProductVariationFilter(filterValue);
      return {
        position: null,
        value: filterValue
      };
    }
    this._captureVariantWizardBaselineState(this._getVariantPickerSizeOptionValues(variantPicker));
    const matchedVariant = this._findMatchedVariantForSelection(variantPicker, selection);
    if (!matchedVariant) {
      variantPicker.clearFitmentFilter?.();
      this._syncVariantWizardFitment({ optionValue: filterValue }, candidateValues, shouldSelectVariant);
      this._syncProductVariationFilter(filterValue);
      return {
        position: null,
        value: filterValue
      };
    }
    const matchedSelection = { ...selection, size: matchedVariant.optionValue };
    this._setResult("success", this._interpolate(matchedSelection.note || this.config.messages.success || "", matchedSelection));
    this._syncCartProperty(matchedSelection);
    const optionValues = this._getVariantOptionValues(variantPicker, matchedVariant.position);
    this._captureVariantWizardResetState(optionValues);
    if (matchedVariant.position && matchedVariant.optionValue) {
      variantPicker.setFitmentFilter?.(matchedVariant.position, matchedVariant.optionValue);
      this._syncVariantWizardFitment(matchedVariant, optionValues, shouldSelectVariant);
      if (shouldSelectVariant) {
        variantPicker.selectVariant(matchedVariant.variant.id);
      }
      this._syncProductVariationFilter(matchedVariant.optionValue);
      return {
        position: matchedVariant.position,
        value: matchedVariant.optionValue
      };
    }
    if (shouldSelectVariant) {
      variantPicker.selectVariant(matchedVariant.variant.id);
    }
    return null;
  }
  _syncVariantWizardFitment(matchedVariant, optionValues = [], shouldSelectVariant = true) {
    if (!matchedVariant?.optionValue) {
      this._clearVariantWizardFitment();
      return;
    }
    this._stopVariantWizardResetRestore();
    this._captureVariantWizardResetState(optionValues);
    this._stopVariantWizardFitment();
    const syncToken = this._fitmentSyncToken;
    const filter = {
      value: matchedVariant.optionValue,
      optionValues
    };
    this._activeVariantWizardFilter = {
      ...filter,
      shouldSelectVariant
    };
    const applyFilter = () => {
      if (syncToken !== this._fitmentSyncToken) {
        return false;
      }
      return this._applyVariantWizardFitment(filter, shouldSelectVariant);
    };
    this._observeVariantWizardFitment(filter, syncToken, shouldSelectVariant);
    applyFilter();
    let attemptCount = 0;
    this._fitmentWizardRetryTimer = window.setInterval(() => {
      attemptCount += 1;
      if (syncToken !== this._fitmentSyncToken || attemptCount > 30) {
        window.clearInterval(this._fitmentWizardRetryTimer);
        this._fitmentWizardRetryTimer = null;
        return;
      }
      applyFilter();
    }, 100);
  }
  _observeVariantWizardFitment(filter, syncToken, shouldSelectVariant) {
    if (!window.MutationObserver) {
      return;
    }
    const container = this._getProductContainer();
    const target = container === document ? document.body : container;
    if (!target) {
      return;
    }
    this._fitmentWizardObserver = new MutationObserver(() => {
      if (this._fitmentWizardApplying || syncToken !== this._fitmentSyncToken) {
        return;
      }
      window.cancelAnimationFrame(this._fitmentWizardApplyFrame);
      this._fitmentWizardApplyFrame = window.requestAnimationFrame(() => {
        if (syncToken === this._fitmentSyncToken) {
          this._applyVariantWizardFitment(filter, shouldSelectVariant);
        }
      });
    });
    this._fitmentWizardObserver.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-selected", "class", "hidden", "style"],
      characterData: true
    });
  }
  _stopVariantWizardFitment() {
    if (this._fitmentWizardRetryTimer) {
      window.clearInterval(this._fitmentWizardRetryTimer);
      this._fitmentWizardRetryTimer = null;
    }
    if (this._fitmentWizardApplyFrame) {
      window.cancelAnimationFrame(this._fitmentWizardApplyFrame);
      this._fitmentWizardApplyFrame = null;
    }
    if (this._fitmentWizardObserver) {
      this._fitmentWizardObserver.disconnect();
      this._fitmentWizardObserver = null;
    }
  }
  _clearVariantWizardFitment() {
    this._stopVariantWizardFitment();
    this._stopVariantWizardResetRestore();
    this._activeVariantWizardFilter = null;
    this._restoreVariantWizardSizeLabels();
    this._restoreVariantWizardControls();
    this._restoreVariantWizardSelection();
  }
  _resetFitmentSizeSelection() {
    const variantPicker = this._getVariantPicker();
    const syncToken = this._beginFitmentSync();
    this._stopVariantWizardFitment();
    this._stopVariantWizardResetRestore();
    this._activeVariantWizardFilter = null;
    this._fitmentResetStateCaptured = false;
    this._fitmentOriginalVariantId = null;
    this._fitmentOriginalSizeValue = null;
    this._fitmentOriginalOptionValues = null;
    variantPicker?.clearFitmentFilter?.();
    const applyReset = () => {
      if (syncToken !== this._fitmentSyncToken) {
        return;
      }
      const resetState = this._getDefaultFitmentSizeResetState(variantPicker);
      this._restoreVariantWizardSizeLabels();
      this._restoreVariantWizardControls();
      this._restoreVariantPickerFitmentControls(variantPicker);
      this._syncProductVariationFilter(null);
      if (resetState?.variantId && variantPicker?.selectedVariant?.id !== resetState.variantId) {
        variantPicker.selectVariant(resetState.variantId);
      }
      if (resetState?.sizeValue) {
        this._syncProductVariationSelection(resetState.sizeValue);
        this._selectVariantWizardValue(resetState.sizeValue, resetState.optionValues);
        this._syncVariantWizardSelectionState(resetState.sizeValue, resetState.optionValues);
        this._syncVariantWizardSizeLabels(resetState.sizeValue, resetState.optionValues, false);
      }
    };
    applyReset();
    window.requestAnimationFrame(applyReset);
    [50, 150, 300, 600, 1000, 1600].forEach((delay) => window.setTimeout(applyReset, delay));
    if (variantPicker?.productPromise && !Array.isArray(variantPicker.product?.variants)) {
      variantPicker.productPromise.then((product) => {
        if (syncToken === this._fitmentSyncToken) {
          variantPicker.product = product;
          applyReset();
        }
      }).catch(() => {
      });
    }
  }
  _restoreVariantPickerFitmentControls(variantPicker) {
    const roots = this._uniqueValues([this._getProductContainer(), variantPicker].filter(Boolean));
    roots.forEach((root) => {
      root.querySelectorAll("[data-fitment-locked]").forEach((item) => item.removeAttribute("data-fitment-locked"));
      root.querySelectorAll("[data-fitment-hidden]").forEach((item) => {
        item.hidden = false;
        item.removeAttribute("data-fitment-hidden");
        item.style.removeProperty("display");
        const control = item.matches?.("input, select, option") ? item : item.querySelector?.("input, select, option");
        if (control) {
          control.disabled = false;
        }
      });
    });
  }
  _getDefaultFitmentSizeResetState(variantPicker) {
    const optionValues = this._getVariantPickerSizeOptionValues(variantPicker);
    const productVariationValues = this._getProductVariationSizeValues();
    const wizardValues = this._getVariantWizardSizeValues(optionValues);
    const sizeValue = optionValues[0] || productVariationValues[0] || wizardValues[0] || this._fitmentBaselineSizeValue || this._fitmentBaselineOptionValues?.[0] || this._getDefaultFitmentSizeValues()[0];
    if (!sizeValue) {
      return null;
    }
    return {
      variantId: this._getVariantPickerVariantIdForSizeValue(variantPicker, sizeValue),
      sizeValue,
      optionValues: this._uniqueValues([...optionValues, ...productVariationValues, ...wizardValues, ...this._getDefaultFitmentSizeValues()])
    };
  }
  _getProductVariationPickers() {
    const container = this.closest(".product-info") || document;
    const pickers = Array.from(container.querySelectorAll("[data-product-variation-picker], .product-info__product-picker .variant-picker__option-values"));
    const sizePickers = pickers.filter((picker) => {
      const optionName = picker.getAttribute?.("data-product-variation-option-name") || picker.closest(".variant-picker__option")?.querySelector(".variant-picker__option-info legend")?.textContent || "";
      return this._isSizeOptionName(optionName);
    });
    return sizePickers;
  }
  _getProductVariationItems(picker) {
    const items = Array.from(picker.querySelectorAll(":scope > [data-product-variation-value]"));
    return items.length ? items : Array.from(picker.children);
  }
  _getProductVariationItemValue(item) {
    if (!item) {
      return "";
    }
    return `${item.getAttribute?.("data-product-variation-value") || item.textContent || ""}`.replace(/\s+/g, " ").trim();
  }
  _getProductVariationSizeValues() {
    const values = [];
    this._getProductVariationPickers().forEach((picker) => {
      this._getProductVariationItems(picker).forEach((item) => {
        const value = this._getProductVariationItemValue(item);
        if (value) {
          values.push(value);
        }
      });
    });
    return this._uniqueValues(values);
  }
  _getVariantWizardSizeValues(optionValues = []) {
    return this._uniqueValues(this._getVariantWizardOptionCandidates(optionValues).map((candidate) => candidate.normalizedValue).filter(Boolean));
  }
  _syncProductVariationSelection(value) {
    const normalizedValue = this._normalizeValue(value);
    if (!normalizedValue) {
      return;
    }
    this._getProductVariationPickers().forEach((picker) => {
      const items = this._getProductVariationItems(picker);
      items.forEach((item) => {
        const isSelected = this._normalizeValue(this._getProductVariationItemValue(item)) === normalizedValue;
        const swatch = item.querySelector?.(".media-swatch, .block-swatch, .color-swatch, [role='option'], [data-option-value]") || item;
        swatch.classList?.toggle("is-selected", isSelected);
        if (swatch.hasAttribute?.("aria-selected")) {
          swatch.setAttribute("aria-selected", isSelected ? "true" : "false");
        }
      });
      const selectedItem = items.find((item) => this._normalizeValue(this._getProductVariationItemValue(item)) === normalizedValue);
      const selectedText = this._getProductVariationItemValue(selectedItem);
      const valueElement = picker.closest(".variant-picker__option")?.querySelector(".variant-picker__option-info .h-stack span");
      if (selectedText && valueElement) {
        valueElement.textContent = selectedText;
      }
    });
  }
  _getVariantPickerVariantIdForSizeValue(variantPicker, sizeValue) {
    const sizeOptionIndex = this._getVariantPickerSizeOptionIndex(variantPicker);
    const targetValue = this._normalizeValue(sizeValue);
    if (!targetValue || !Array.isArray(variantPicker?.product?.variants)) {
      return null;
    }
    const variantOptionCount = variantPicker.product.variants[0]?.options?.length || 0;
    const optionIndexes = sizeOptionIndex === -1 ? Array.from({ length: variantOptionCount }, (_value, index) => index) : [sizeOptionIndex];
    const currentOptions = variantPicker.selectedVariant?.options || [];
    let bestVariant = null;
    variantPicker.product.variants.forEach((variant) => {
      if (!variant.options) {
        return;
      }
      if (!variant.available && variantPicker.hideSoldOutVariants) {
        return;
      }
      optionIndexes.forEach((optionIndex) => {
        if (this._normalizeValue(variant.options[optionIndex]) !== targetValue) {
          return;
        }
        const sameOtherOptionsCount = currentOptions.reduce((count, optionValue, currentOptionIndex) => {
          if (!optionValue || currentOptionIndex === optionIndex) {
            return count;
          }
          return count + (variant.options[currentOptionIndex] === optionValue ? 1 : 0);
        }, 0);
        const score = (variant.available ? 100 : 0) + sameOtherOptionsCount;
        if (!bestVariant || score > bestVariant.score) {
          bestVariant = {
            id: variant.id,
            score
          };
        }
      });
    });
    return bestVariant?.id || null;
  }
  _restoreVariantWizardControls() {
    this._getProductContainer().querySelectorAll("[data-fitment-hidden], [data-fitment-selected]").forEach((item) => {
      item.hidden = false;
      item.removeAttribute("data-fitment-hidden");
      item.removeAttribute("data-fitment-selected");
      item.style.removeProperty("display");
      item.querySelectorAll?.("input, select, option, button, [role='option']").forEach((control) => {
        control.disabled = false;
        control.removeAttribute("aria-disabled");
      });
    });
  }
  _applyVariantWizardFitment(filter, shouldSelectVariant) {
    const targetValue = this._normalizeValue(filter.value);
    if (!targetValue) {
      return false;
    }
    const candidates = this._getVariantWizardOptionCandidates(filter.optionValues);
    const managedCandidates = candidates.filter((candidate) => this._isManagedVariantWizardValue(candidate.normalizedValue, filter.optionValues));
    const matchedCandidates = managedCandidates.filter((candidate) => candidate.normalizedValue === targetValue);
    if (!matchedCandidates.length) {
      return false;
    }
    matchedCandidates.sort((firstCandidate, secondCandidate) => {
      return Number(secondCandidate.rawNormalizedValue === targetValue) - Number(firstCandidate.rawNormalizedValue === targetValue);
    });
    this._fitmentWizardApplying = true;
    try {
      matchedCandidates.forEach((candidate) => this._setFitmentHidden(candidate.item, false));
      if (shouldSelectVariant) {
        matchedCandidates.forEach((candidate) => this._selectVariantWizardCandidate(candidate, filter.value));
        this._syncVariantWizardSelectionState(filter.value, filter.optionValues);
      }
      managedCandidates.forEach((candidate) => {
        const shouldHide = candidate.normalizedValue !== targetValue;
        this._setFitmentHidden(candidate.item, shouldHide);
        if (shouldHide) {
          candidate.item.removeAttribute("data-fitment-selected");
          this._clearVariantWizardSelectionMarker(candidate);
        }
      });
      this._syncVariantWizardSizeLabels(filter.value, filter.optionValues);
    } finally {
      this._fitmentWizardApplying = false;
    }
    return true;
  }
  _getVariantWizardOptionCandidates(optionValues) {
    const container = this._getProductContainer();
    const selectors = [
      ".rio-options input[value]",
      ".rio-options option[value]",
      ".rio-options button",
      ".rio-options label",
      ".rio-options [role='option']",
      ".rio-options [data-option-value]",
      ".rio-options [data-value]",
      ".rio-options .pf-vs-square",
      ".rio-options .pf-option-swatch",
      ".rio-options [data-pf-type='Swatch']",
      ".rio-options [data-option-name]",
      ".rio-options span",
      ".rio-options div",
      ".rio-variants input[value]",
      ".rio-variants button",
      ".rio-variants label",
      ".rio-variants [role='option']",
      ".rio-variants [data-option-value]",
      ".rio-variants [data-value]",
      ".rio-variants [data-pf-type='Swatch']",
      ".rio-variants [data-option-name]",
      ".rio-variants span",
      ".rio-variants div",
      ".pf-option-swatches input[value]",
      ".pf-option-swatches option[value]",
      ".pf-option-swatches button",
      ".pf-option-swatches label",
      ".pf-option-swatches [role='option']",
      ".pf-option-swatches [data-option-value]",
      ".pf-option-swatches [data-value]",
      ".pf-option-swatches .pf-vs-square",
      ".pf-option-swatches .pf-option-swatch",
      ".pf-option-swatches [data-pf-type='Swatch']",
      ".pf-option-swatches [data-option-name]",
      ".pf-option-swatches span",
      ".pf-option-swatches div",
      ".product-info__variant-picker input[name^='option']",
      ".product-info__variant-picker option[value]",
      ".product-info__variant-picker label",
      ".product-info__variant-picker [data-option-value]",
      "[data-pf-type='ProductVariant'] input[value]",
      "[data-pf-type='ProductVariant'] option[value]",
      "[data-pf-type='ProductVariant'] label",
      "[data-pf-type='ProductVariant'] [role='option']",
      "[data-pf-type='ProductVariant'] [data-option-value]",
      "[data-pf-type='ProductVariant'] [data-value]",
      "[data-pf-type='ProductVariant'] [data-pf-type='Swatch']",
      "[data-pf-type='ProductVariant'] .pf-vs-square",
      "[data-pf-type='ProductVariant'] .pf-option-swatch",
      "[data-pf-type='ProductVariant'] span",
      "[data-pf-type='ProductVariant'] div",
      "[data-option-name='Size']",
      "[data-option-name='SIZE']"
    ];
    const candidates = [];
    const seenItems = new Set();
    container.querySelectorAll(selectors.join(",")).forEach((control) => {
      const controlValue = this._getVariantWizardControlValue(control);
      const rawNormalizedValue = this._normalizeValue(controlValue).replace(/\s+/g, " ");
      const normalizedValue = this._extractVariantWizardValue(controlValue, optionValues);
      if (!normalizedValue || !this._isVariantWizardValueCandidate(control, rawNormalizedValue, normalizedValue, optionValues)) {
        return;
      }
      const item = this._getVariantWizardItem(control);
      if (!item || seenItems.has(item)) {
        return;
      }
      seenItems.add(item);
      candidates.push({
        control,
        item,
        normalizedValue,
        rawNormalizedValue
      });
    });
    return candidates;
  }
  _isVariantWizardValueCandidate(control, rawValue, normalizedValue, optionValues) {
    if (!this._isManagedVariantWizardValue(normalizedValue, optionValues)) {
      return false;
    }
    if (rawValue === normalizedValue) {
      return true;
    }
    if (control.querySelectorAll?.("input[value]").length > 1) {
      return false;
    }
    if (control.tagName === "LABEL" && control.htmlFor) {
      return true;
    }
    if (control.querySelector?.("input[value]")) {
      return true;
    }
    return ["data-option-value", "data-value", "data-variant-option-value", "value", "aria-label", "title"].some((attributeName) => control.hasAttribute?.(attributeName));
  }
  _captureVariantWizardResetState(optionValues) {
    if (this._fitmentResetStateCaptured) {
      return;
    }
    this._fitmentResetStateCaptured = true;
    const variantPicker = this._getVariantPicker();
    const resolvedOptionValues = optionValues?.length ? optionValues : this._getVariantPickerSizeOptionValues(variantPicker);
    this._fitmentOriginalVariantId = variantPicker?.selectedVariant?.id || null;
    this._fitmentOriginalSizeValue = this._getCurrentVariantWizardSizeValue(resolvedOptionValues) || this._getVariantPickerSelectedSizeValue(variantPicker, resolvedOptionValues);
    this._fitmentOriginalOptionValues = resolvedOptionValues;
  }
  _captureVariantWizardBaselineState(optionValues = []) {
    if (this._activeVariantWizardFilter?.value) {
      return;
    }
    const variantPicker = this._getVariantPicker();
    const resolvedOptionValues = optionValues.length ? optionValues : this._getVariantPickerSizeOptionValues(variantPicker);
    const variantId = variantPicker?.selectedVariant?.id || null;
    const sizeValue = this._getCurrentVariantWizardSizeValue(resolvedOptionValues) || this._getVariantPickerSelectedSizeValue(variantPicker, resolvedOptionValues);
    if (!variantId && !sizeValue) {
      return;
    }
    if (this._fitmentBaselineStateCaptured) {
      this._fitmentBaselineVariantId = this._fitmentBaselineVariantId || variantId;
      this._fitmentBaselineSizeValue = this._fitmentBaselineSizeValue || sizeValue;
      this._fitmentBaselineOptionValues = this._fitmentBaselineOptionValues?.length ? this._fitmentBaselineOptionValues : resolvedOptionValues;
      return;
    }
    this._fitmentBaselineStateCaptured = true;
    this._fitmentBaselineVariantId = variantId;
    this._fitmentBaselineSizeValue = sizeValue;
    this._fitmentBaselineOptionValues = resolvedOptionValues;
  }
  _getVariantWizardRestoreState() {
    if (this._fitmentResetStateCaptured) {
      return {
        variantId: this._fitmentOriginalVariantId,
        sizeValue: this._fitmentOriginalSizeValue,
        optionValues: this._fitmentOriginalOptionValues,
        clearAfterRestore: true
      };
    }
    if (this._fitmentBaselineStateCaptured) {
      return {
        variantId: this._fitmentBaselineVariantId,
        sizeValue: this._fitmentBaselineSizeValue,
        optionValues: this._fitmentBaselineOptionValues,
        clearAfterRestore: false
      };
    }
    return null;
  }
  _restoreVariantWizardSelection() {
    const restoreState = this._getVariantWizardRestoreState();
    if (!restoreState) {
      return;
    }
    const restoreSelection = () => {
      this._restoreVariantWizardControls();
      const variantPicker = this._getVariantPicker();
      if (restoreState.variantId && variantPicker) {
        variantPicker.selectVariant(restoreState.variantId);
      }
      if (restoreState.sizeValue) {
        this._selectVariantWizardValue(restoreState.sizeValue, restoreState.optionValues);
        this._syncVariantWizardSelectionState(restoreState.sizeValue, restoreState.optionValues);
        this._syncVariantWizardSizeLabels(restoreState.sizeValue, restoreState.optionValues, false);
      }
    };
    restoreSelection();
    let attemptCount = 0;
    this._fitmentWizardResetTimer = window.setInterval(() => {
      attemptCount += 1;
      if (attemptCount > 12) {
        this._stopVariantWizardResetRestore();
        return;
      }
      restoreSelection();
    }, 100);
    if (restoreState.clearAfterRestore) {
      this._fitmentResetStateCaptured = false;
      this._fitmentOriginalVariantId = null;
      this._fitmentOriginalSizeValue = null;
      this._fitmentOriginalOptionValues = null;
    }
  }
  _stopVariantWizardResetRestore() {
    if (this._fitmentWizardResetTimer) {
      window.clearInterval(this._fitmentWizardResetTimer);
      this._fitmentWizardResetTimer = null;
    }
  }
  _getCurrentVariantWizardSizeValue(optionValues) {
    const candidates = this._getVariantWizardOptionCandidates(optionValues);
    const selectedCandidate = candidates.find((candidate) => this._isVariantWizardCandidateSelected(candidate));
    return selectedCandidate?.normalizedValue || this._getVariantWizardLabelSizeValue(optionValues) || "";
  }
  _getVariantPickerSizeOptionIndex(variantPicker) {
    const options = Array.isArray(variantPicker?.product?.options) ? variantPicker.product.options : [];
    const namedSizeIndex = options.findIndex((option) => this._isSizeOptionName(option?.name || option));
    if (namedSizeIndex !== -1) {
      return namedSizeIndex;
    }
    const variantOptionCount = variantPicker?.product?.variants?.[0]?.options?.length || 0;
    const managedValues = this._getDefaultFitmentSizeValues();
    for (let optionIndex = 0; optionIndex < variantOptionCount; optionIndex += 1) {
      const optionValues = this._getVariantOptionValues(variantPicker, optionIndex + 1);
      if (optionValues.some((value) => managedValues.includes(this._normalizeValue(value)))) {
        return optionIndex;
      }
    }
    return -1;
  }
  _getVariantPickerSizeOptionValues(variantPicker) {
    const sizeOptionIndex = this._getVariantPickerSizeOptionIndex(variantPicker);
    if (sizeOptionIndex === -1) {
      return [];
    }
    return this._getVariantOptionValues(variantPicker, sizeOptionIndex + 1);
  }
  _getVariantPickerSelectedSizeValue(variantPicker, optionValues = []) {
    const selectedOptions = variantPicker?.selectedVariant?.options || [];
    if (!selectedOptions.length) {
      return "";
    }
    const managedValues = this._getNormalizedManagedOptionValues(optionValues);
    const productOptions = Array.isArray(variantPicker?.product?.options) ? variantPicker.product.options : [];
    const sizeIndexes = productOptions.reduce((indexes, option, index) => {
      if (this._isSizeOptionName(option?.name || option)) {
        indexes.push(index);
      }
      return indexes;
    }, []);
    const indexes = sizeIndexes.length ? sizeIndexes : selectedOptions.map((_value, index) => index);
    const matchedIndex = indexes.find((index) => managedValues.includes(this._normalizeValue(selectedOptions[index]).replace(/\s+/g, " ")));
    return matchedIndex === void 0 ? "" : selectedOptions[matchedIndex];
  }
  _selectVariantWizardValue(value, optionValues) {
    const normalizedValue = this._normalizeValue(value);
    if (!normalizedValue) {
      return false;
    }
    const candidates = this._getVariantWizardOptionCandidates(optionValues).filter((item) => item.normalizedValue === normalizedValue && !item.item.hidden);
    const selected = candidates.reduce((wasSelected, candidate) => {
      return this._selectVariantWizardCandidate(candidate, value) || wasSelected;
    }, false);
    if (candidates.length) {
      this._syncVariantWizardSelectionState(value, optionValues);
    }
    return selected;
  }
  _getVariantWizardSelectionElements(candidate) {
    if (!candidate) {
      return [];
    }
    const linkedInput = this._getVariantWizardCandidateInput(candidate);
    const linkedLabel = linkedInput?.id ? this._getProductContainer().querySelector(`label[for="${CSS.escape(linkedInput.id)}"]`) : null;
    return this._uniqueValues([
      candidate.item,
      candidate.control,
      linkedLabel,
      this._getVariantWizardClickable(candidate)
    ].filter(Boolean));
  }
  _getVariantWizardCandidateInput(candidate) {
    const control = candidate?.control;
    if (!control) {
      return null;
    }
    if (control.tagName === "LABEL" && control.htmlFor) {
      return control.ownerDocument.getElementById(control.htmlFor);
    }
    if (control.matches?.("input[type='radio'], input[type='checkbox']")) {
      return control;
    }
    return candidate.item?.querySelector?.("input[type='radio'], input[type='checkbox']") || control.querySelector?.("input[type='radio'], input[type='checkbox']") || null;
  }
  _clearVariantWizardSelectionMarker(candidate) {
    const selectedClassNames = ["is-selected", "selected", "active", "is-active", "pf-selected", "pf-active"];
    this._getVariantWizardSelectionElements(candidate).forEach((element) => {
      element.classList?.remove(...selectedClassNames);
      if (element.hasAttribute?.("data-active")) {
        element.setAttribute("data-active", "false");
      }
      if (element.hasAttribute?.("aria-current")) {
        element.removeAttribute("aria-current");
      }
    });
  }
  _syncVariantWizardAriaSelection(candidate, isSelected) {
    this._getVariantWizardSelectionElements(candidate).forEach((element) => {
      if (isSelected || element.hasAttribute?.("data-active")) {
        element.setAttribute("data-active", isSelected ? "true" : "false");
      }
      if (element.hasAttribute?.("aria-selected") || element.getAttribute?.("role") === "option") {
        element.setAttribute("aria-selected", isSelected ? "true" : "false");
      }
      if (element.hasAttribute?.("aria-checked")) {
        element.setAttribute("aria-checked", isSelected ? "true" : "false");
      }
    });
  }
  _syncVariantWizardSelectionState(value, optionValues) {
    const normalizedValue = this._normalizeValue(value);
    if (!normalizedValue) {
      return;
    }
    this._getVariantWizardOptionCandidates(optionValues).forEach((candidate) => {
      const isSelected = candidate.normalizedValue === normalizedValue;
      const input = this._getVariantWizardCandidateInput(candidate);
      if (input && (input.type === "radio" || input.type === "checkbox")) {
        const wasChecked = input.checked;
        input.checked = isSelected;
        if (isSelected && !wasChecked) {
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
      if (isSelected) {
        candidate.item.setAttribute("data-fitment-selected", "");
      } else {
        candidate.item.removeAttribute("data-fitment-selected");
        this._clearVariantWizardSelectionMarker(candidate);
      }
      this._syncVariantWizardAriaSelection(candidate, isSelected);
    });
  }
  _getVariantWizardLabelSizeValue(optionValues) {
    const managedValues = this._getNormalizedManagedOptionValues(optionValues);
    const valuePattern = managedValues.map((managedValue) => this._escapeRegExp(managedValue)).join("|");
    const sizeLabelPattern = new RegExp(`\\bSize\\s*:\\s*(${valuePattern})(\\b)`, "i");
    const standaloneValuePattern = new RegExp(`^\\s*(${valuePattern})\\s*$`, "i");
    for (const labelElement of this._getVariantWizardSizeLabelElements()) {
      const inlineMatch = labelElement.textContent?.match(sizeLabelPattern);
      if (inlineMatch) {
        return this._normalizeValue(inlineMatch[1]);
      }
      const valueElement = this._getVariantWizardLabelValueElements(labelElement, standaloneValuePattern)[0];
      const standaloneMatch = valueElement?.textContent?.match(standaloneValuePattern);
      if (standaloneMatch) {
        return this._normalizeValue(standaloneMatch[1]);
      }
    }
    return "";
  }
  _syncVariantWizardSizeLabels(value, optionValues, captureOriginal = true) {
    const normalizedValue = this._normalizeValue(value);
    if (!normalizedValue) {
      this._restoreVariantWizardSizeLabels();
      return;
    }
    const managedValues = this._getNormalizedManagedOptionValues(optionValues);
    const valuePattern = managedValues.map((managedValue) => this._escapeRegExp(managedValue)).join("|");
    const sizeLabelPattern = new RegExp(`(\\bSize\\s*:\\s*)(${valuePattern})(\\b)`, "i");
    const standaloneValuePattern = new RegExp(`^\\s*(${valuePattern})\\s*$`, "i");
    this._getVariantWizardSizeLabelElements().forEach((element) => {
      const childTextNodes = Array.from(element.childNodes || []).filter((node) => node.nodeType === 3);
      const matchingTextNodes = childTextNodes.filter((node) => sizeLabelPattern.test(node.textContent || ""));
      matchingTextNodes.forEach((textNode) => {
        if (captureOriginal && !element.hasAttribute("data-fitment-original-size-label")) {
          element.setAttribute("data-fitment-original-size-label", textNode.textContent || "");
          element.setAttribute("data-fitment-original-size-label-index", `${childTextNodes.indexOf(textNode)}`);
        }
        const nextText = textNode.textContent.replace(sizeLabelPattern, `$1${normalizedValue}`);
        if (textNode.textContent !== nextText) {
          textNode.textContent = nextText;
        }
      });
      const standaloneValueElements = matchingTextNodes.length ? [] : this._getVariantWizardLabelValueElements(element, standaloneValuePattern);
      standaloneValueElements.forEach((valueElement) => {
        if (captureOriginal && !valueElement.hasAttribute("data-fitment-original-size-label")) {
          valueElement.setAttribute("data-fitment-original-size-label", valueElement.textContent || "");
        }
        if (valueElement.textContent !== normalizedValue) {
          valueElement.textContent = normalizedValue;
        }
      });
      if (!matchingTextNodes.length && !standaloneValueElements.length && element.children.length === 0 && sizeLabelPattern.test(element.textContent || "")) {
        if (captureOriginal && !element.hasAttribute("data-fitment-original-size-label")) {
          element.setAttribute("data-fitment-original-size-label", element.textContent || "");
        }
        const nextText = element.textContent.replace(sizeLabelPattern, `$1${normalizedValue}`);
        if (element.textContent !== nextText) {
          element.textContent = nextText;
        }
      } else if (!matchingTextNodes.length && !standaloneValueElements.length && element.children.length <= 2 && sizeLabelPattern.test(element.textContent || "") && !this._hasVariantWizardOptionControls(element)) {
        if (captureOriginal && !element.hasAttribute("data-fitment-original-size-label")) {
          element.setAttribute("data-fitment-original-size-label", element.textContent || "");
        }
        const nextText = element.textContent.replace(sizeLabelPattern, `$1${normalizedValue}`);
        if (element.textContent !== nextText) {
          element.textContent = nextText;
        }
      }
    });
  }
  _getVariantWizardSizeLabelElements() {
    const selectors = [
      ".pf-variant-label",
      ".pf-option-label",
      "[data-pf-type='OptionLabel']",
      "[data-option-name='Size']",
      "[data-option-name='SIZE']",
      "[class*='variant-label']",
      "[class*='option-label']",
      ".rio-options *",
      ".rio-variants *",
      ".pf-option-swatches *",
      "[data-pf-type='ProductVariant'] *",
      "label",
      "p",
      "span",
      "div"
    ];
    return Array.from(this._getProductContainer().querySelectorAll(selectors.join(","))).filter((element) => {
      const text = `${element.textContent || ""}`.replace(/\s+/g, " ").trim();
      return text.length <= 40 && (/\bSize\s*:/i.test(text) || /^Size$/i.test(text)) && !this._hasVariantWizardOptionControls(element);
    });
  }
  _getVariantWizardLabelValueElements(labelElement, valuePattern) {
    const valueElements = [];
    const collectValueElement = (element) => {
      if (!element || element === labelElement || valueElements.includes(element)) {
        return;
      }
      const text = `${element.textContent || ""}`.replace(/\s+/g, " ").trim();
      if (text.length > 20 || !valuePattern.test(text) || this._hasVariantWizardOptionControls(element)) {
        return;
      }
      valueElements.push(element);
    };
    Array.from(labelElement.children || []).forEach(collectValueElement);
    collectValueElement(labelElement.nextElementSibling);
    Array.from(labelElement.parentElement?.children || []).forEach(collectValueElement);
    return valueElements;
  }
  _hasVariantWizardOptionControls(element) {
    const optionControlSelector = "input, select, option, button, img, .pf-vs-square, .pf-option-swatches, .pf-option-swatch, .rio-swatch, .swatch, [data-pf-type='Swatch'], [role='option']";
    return Boolean(element.matches?.(optionControlSelector) || element.querySelector?.(optionControlSelector));
  }
  _restoreVariantWizardSizeLabels() {
    this._getProductContainer().querySelectorAll("[data-fitment-original-size-label]").forEach((element) => {
      const originalLabel = element.getAttribute("data-fitment-original-size-label") || "";
      const textNodeIndex = parseInt(element.getAttribute("data-fitment-original-size-label-index") || "0", 10);
      const textNodes = Array.from(element.childNodes || []).filter((node) => node.nodeType === 3);
      const textNode = textNodes[textNodeIndex] || textNodes.find((node) => /\bSize\s*:/i.test(node.textContent || ""));
      if (textNode) {
        textNode.textContent = originalLabel;
      } else if (element.children.length === 0) {
        element.textContent = originalLabel;
      }
      element.removeAttribute("data-fitment-original-size-label");
      element.removeAttribute("data-fitment-original-size-label-index");
    });
  }
  _getNormalizedManagedOptionValues(optionValues) {
    const values = optionValues?.length ? [...optionValues, ...this._getDefaultFitmentSizeValues()] : this._getDefaultFitmentSizeValues();
    return this._uniqueValues(values.map((value) => this._normalizeValue(value).replace(/\s+/g, " ")).filter(Boolean)).sort((a, b) => b.length - a.length);
  }
  _getDefaultFitmentSizeValues() {
    return ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
  }
  _isManagedVariantWizardValue(value, optionValues) {
    const managedValues = this._getNormalizedManagedOptionValues(optionValues);
    return managedValues.includes(value) || this._getFitmentSizeRank(value) !== -1;
  }
  _extractVariantWizardValue(value, optionValues) {
    const normalizedValue = this._normalizeValue(value).replace(/\s+/g, " ");
    if (!normalizedValue) {
      return "";
    }
    const managedValues = this._getNormalizedManagedOptionValues(optionValues);
    const exactValue = managedValues.find((managedValue) => managedValue === normalizedValue);
    if (exactValue) {
      return exactValue;
    }
    const containedValue = managedValues.find((managedValue) => new RegExp(`(^|[^A-Z0-9])${this._escapeRegExp(managedValue)}([^A-Z0-9]|$)`).test(normalizedValue));
    return containedValue || normalizedValue;
  }
  _escapeRegExp(value) {
    return `${value}`.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
  _getVariantWizardControlValue(control) {
    if (control.tagName === "LABEL") {
      // 该主题 input value 为 option_value.id（数字 ID），优先取 label 文本获得可读尺寸名（如 "S"/"L"）
      const labelText = `${control.textContent || ""}`.replace(/\s+/g, " ").trim();
      if (labelText) {
        return labelText;
      }
      if (control.htmlFor) {
        const input = control.ownerDocument.getElementById(control.htmlFor);
        if (input?.value) {
          return input.value;
        }
      }
    }
    for (const attributeName of ["data-option-value", "data-value", "data-variant-option-value", "value", "aria-label", "title"]) {
      const attributeValue = control.getAttribute?.(attributeName);
      if (attributeValue) {
        return attributeValue;
      }
    }
    const input = control.querySelector?.("input[value]");
    if (input?.value) {
      return input.value;
    }
    return control.textContent || "";
  }
  _getVariantWizardItem(control) {
    const structuralItemSelector = "[data-pf-type='Swatch'], .pf-vs-square, .pf-option-swatch, .rio-swatch, .swatch, li";
    const fallbackItemSelector = "option, label, button, [role='option'], [data-option-value], [data-value], [data-option-name]";
    if (control.tagName === "INPUT" && control.id) {
      const label = this._getProductContainer().querySelector(`label[for="${CSS.escape(control.id)}"]`);
      const structuralItem = control.closest(structuralItemSelector) || label?.closest(structuralItemSelector);
      if (structuralItem) {
        return structuralItem;
      }
      if (label) {
        return label.closest(fallbackItemSelector) || label;
      }
    }
    const structuralItem = control.closest?.(structuralItemSelector);
    if (structuralItem) {
      return structuralItem;
    }
    return control.matches?.(fallbackItemSelector) ? control : control.closest?.(fallbackItemSelector) || control;
  }
  _selectVariantWizardCandidate(candidate, value) {
    if (this._isVariantWizardCandidateSelected(candidate)) {
      return false;
    }
    const control = candidate.control;
    if (control.tagName === "OPTION") {
      const select = control.parentElement;
      if (select?.tagName === "SELECT" && select.value !== control.value) {
        select.value = control.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
      candidate.item.setAttribute("data-fitment-selected", "");
      return true;
    }
    if (control.tagName === "INPUT") {
      if (control.type === "radio" || control.type === "checkbox") {
        control.click();
        if (!control.checked) {
          control.checked = true;
          control.dispatchEvent(new Event("change", { bubbles: true }));
        }
      } else if (control.value !== value) {
        control.value = value;
        control.dispatchEvent(new Event("input", { bubbles: true }));
        control.dispatchEvent(new Event("change", { bubbles: true }));
      }
      candidate.item.setAttribute("data-fitment-selected", "");
      return true;
    }
    const clickable = this._getVariantWizardClickable(candidate);
    if (clickable) {
      clickable.click();
      candidate.item.setAttribute("data-fitment-selected", "");
      return true;
    }
    return false;
  }
  _getVariantWizardClickable(candidate) {
    const clickableSelector = "label, button, [role='option']";
    if (candidate.control.matches?.(clickableSelector)) {
      return candidate.control;
    }
    if (candidate.item.matches?.(clickableSelector)) {
      return candidate.item;
    }
    return candidate.item.querySelector?.(clickableSelector) || candidate.control;
  }
  _isVariantWizardCandidateSelected(candidate) {
    const input = this._getVariantWizardCandidateInput(candidate);
    if ((input?.type === "radio" || input?.type === "checkbox") && input.checked) {
      return true;
    }
    return this._getVariantWizardSelectionElements(candidate).some((element) => {
      if (element.getAttribute?.("aria-selected") === "true" || element.getAttribute?.("aria-checked") === "true" || element.getAttribute?.("data-active") === "true") {
        return true;
      }
      return Array.from(element.classList || []).some((className) => ["is-selected", "selected", "active", "is-active", "pf-selected", "pf-active"].includes(className));
    });
  }
  _setFitmentHidden(item, hidden) {
    item.toggleAttribute("data-fitment-hidden", hidden);
    item.hidden = hidden;
    if (hidden) {
      item.style.setProperty("display", "none", "important");
    } else {
      item.style.removeProperty("display");
    }
  }
  _syncProductVariationFilter(value) {
    this._getProductVariationPickers().forEach((picker) => {
      const items = this._getProductVariationItems(picker);
      const normalizedValue = this._normalizeValue(value);
      let matchedCount = 0;
      items.forEach((item) => {
        const itemValue = this._normalizeValue(this._getProductVariationItemValue(item));
        const shouldShow = !normalizedValue || itemValue === normalizedValue;
        item.toggleAttribute("data-fitment-hidden", Boolean(normalizedValue) && !shouldShow);
        item.hidden = !shouldShow;
        if (shouldShow) {
          item.style.removeProperty("display");
        }
        if (shouldShow) {
          matchedCount += 1;
        }
      });
      if (normalizedValue && matchedCount === 0) {
        items.forEach((item) => {
          item.removeAttribute("data-fitment-hidden");
          item.hidden = false;
          item.style.removeProperty("display");
        });
      }
    });
  }
  _syncCartProperty(selection) {
    if (!this.cartPropertyInput) {
      return;
    }
    if (!selection || !selection.vehicle) {
      this.cartPropertyInput.value = "";
      this.cartPropertyInput.disabled = true;
      if (this.sizePropertyInput) {
        this.sizePropertyInput.value = "";
        this.sizePropertyInput.disabled = true;
      }
      return;
    }
    this.cartPropertyInput.value = selection.vehicle;
    this.cartPropertyInput.disabled = false;
    if (this.sizePropertyInput) {
      this.sizePropertyInput.value = selection.size || "";
      this.sizePropertyInput.disabled = !selection.size;
    }
  }
  _getFitmentSizeCandidates(selection) {
    return this._uniqueValues([selection?.size].map((value) => `${value || ""}`.trim()).filter(Boolean));
  }
  _getFitmentFilterCandidates(filter, displayValue) {
    return this._uniqueValues([
      filter?.value,
      ...(Array.isArray(displayValue) ? displayValue : [displayValue])
    ].map((value) => `${value || ""}`.trim()).filter(Boolean));
  }
  _getPreferredFitmentOptionIndexes(variantPicker) {
    const options = Array.isArray(variantPicker.product?.options) ? variantPicker.product.options : [];
    const variantOptionCount = variantPicker.product?.variants?.[0]?.options?.length || 0;
    const allIndexes = Array.from({ length: variantOptionCount }, (_value, index) => index);
    const sizeIndexes = options.reduce((indexes, option, index) => {
      if (this._isSizeOptionName(option?.name || option)) {
        indexes.push(index);
      }
      return indexes;
    }, []);
    return this._uniqueValues([...sizeIndexes, ...allIndexes]);
  }
  _findMatchedVariantForSelection(variantPicker, selection) {
    const candidateValues = this._getFitmentSizeCandidates(selection);
    if (!candidateValues.length) {
      return null;
    }
    const normalizedCandidates = candidateValues.map((value) => this._normalizeValue(value));
    const currentOptions = variantPicker.selectedVariant?.options || [];
    const optionIndexes = this._getPreferredFitmentOptionIndexes(variantPicker);
    let bestMatch = null;
    variantPicker.product.variants.forEach((variant) => {
      if (!variant.options || !(variant.available || !variantPicker.hideSoldOutVariants)) {
        return;
      }
      optionIndexes.forEach((optionIndex) => {
        const optionValue = variant.options[optionIndex];
        const candidateIndex = normalizedCandidates.indexOf(this._normalizeValue(optionValue));
        if (candidateIndex === -1) {
          return;
        }
        const sameOtherOptionsCount = currentOptions.reduce((count, currentOptionValue, currentOptionIndex) => {
          if (!currentOptionValue || currentOptionIndex === optionIndex) {
            return count;
          }
          return count + (variant.options[currentOptionIndex] === currentOptionValue ? 1 : 0);
        }, 0);
        const score = (candidateValues.length - candidateIndex) * 100 + sameOtherOptionsCount;
        if (!bestMatch || score > bestMatch.score) {
          bestMatch = {
            variant,
            position: optionIndex + 1,
            optionValue: variant.options[optionIndex],
            score,
            matchType: "exact"
          };
        }
      });
    });
    if (bestMatch) {
      return bestMatch;
    }
    return null;
  }
  _toggleResetButton() {
    if (!this.resetButton) {
      return;
    }
    const hasSelection = Object.values(this.selects).some((select) => select && select.value);
    this.resetButton.hidden = !hasSelection;
  }
  _onResetClicked() {
    if (this.selects.make) {
      this.selects.make.value = "";
    }
    this._handleMakeChanged();
  }
  _onVariantPickerReady(event) {
    if (event.target?.getAttribute?.("form") !== this.formId) {
      return;
    }
    this._captureVariantWizardBaselineState(this._getVariantPickerSizeOptionValues(event.target));
    const selection = this._getSelection();
    if (selection.make && selection.model && selection.year) {
      this._updateSelectionResult();
    }
  }
  _onPageShow(event) {
    if (event.persisted && this._entriesLoaded) {
      this._initializeState();
    }
  }
  _onProductVariantChanged(event) {
    if (event.target !== document.forms[this.formId] || !this._activeVariantWizardFilter?.value) {
      return;
    }
    const syncToken = this._fitmentSyncToken;
    const filter = {
      value: this._activeVariantWizardFilter.value,
      optionValues: this._activeVariantWizardFilter.optionValues
    };
    const shouldSelectVariant = this._activeVariantWizardFilter.shouldSelectVariant !== false;
    const applyFilter = () => {
      if (syncToken === this._fitmentSyncToken && this._activeVariantWizardFilter?.value === filter.value) {
        this._applyVariantWizardFitment(filter, shouldSelectVariant);
      }
    };
    window.requestAnimationFrame(applyFilter);
    window.setTimeout(applyFilter, 50);
    window.setTimeout(applyFilter, 200);
  }
};
if (!window.customElements.get("vehicle-fitment-search")) {
  window.customElements.define("vehicle-fitment-search", VehicleFitmentSearch);
}
